import httpClient, { ConexaResponse } from '@/services/httpClient'
import {
  IUser,
  IUserData,
  ClinicAndFlags,
  IUserDataResponse,
  MagicLinkLoginResponse,
  SendTwoFactorAuthCodeResponse,
  SendTwoFactorAuthCode,
  IGeolocation,
  IUserDataInfo,
  IUserDataInfoResponse,
  LegalGuardianRelationship,
  MagicLinkByAccount,
} from '@/modules/Auth/auth-types'
import axios from 'axios'
import SendMinorAccessRequest from './mappers/SendMinorAccessRequest'
import {
  AppointmentConfirmation,
  AppointmentConfirmationParams,
} from './types/en-us'
import GetAppointmentConfirmationData from './mappers/GetAppointmentConfirmationData'
import SaveAcceptTermData from './mappers/SaveAcceptTermData'
import { TermsAndConditionsForm } from './components/TermsAndConditionsModal/types'
import {
  MinorAccessRequestData,
  MinorsTermData,
} from './components/MinorAccessRequestModal/types'
import GetMinorsTermAcceptanceData from './mappers/GetMinorsTermAcceptanceData'
import { getCache } from '@/utils/cache'
import GetUserData from './mappers/GetUserData'
import GetAuthMagicLinkByAccount from './mappers/GetAuthMagicLinkByAccount'

export default {
  async login(
    user: string,
    pass: string,
    captchaToken: string,
    useRefreshToken = false,
  ): Promise<IUser[]> {
    return httpClient
      .post(
        '/integration/app-paciente/usuario/login/web',
        {
          email: user,
          senha: pass,
          audience: 'WEB',
          utilizaRefreshToken: useRefreshToken,
        },
        {
          headers: {
            captcha: captchaToken,
          },
        },
      )
      .then(res => {
        return res.data.object
      })
  },

  async getClinic(userToken: string): Promise<ClinicAndFlags> {
    return httpClient
      .get('/integration/app-paciente/clinica/configuracoes', {
        headers: {
          token: userToken,
        },
      })
      .then(res => {
        const object = JSON.parse(JSON.stringify(res.data.object))
        object.clinic = {}
        object.flags = {}
        Object.keys(res.data.object).forEach(function (item) {
          if (item !== 'flags' && item !== 'clinic') {
            if (item.includes('flag')) {
              object.flags[item.replace('flag', '')] = res.data.object[item]
              delete object[item]
            } else {
              object.clinic[item] = object[item]
              delete object[item]
            }
          }
        })
        return object
      })
  },

  async getUserIP(): Promise<string> {
    return axios.get('https://www.cloudflare.com/cdn-cgi/trace').then(res => {
      return res.data.split('\n')[2].split('=')[1]
    })
  },

  async getUserData(): Promise<IUserData> {
    const response = await httpClient.get<ConexaResponse<IUserDataResponse>>(
      '/usuario/meusdados',
    )

    return GetUserData.toDomain(response?.data?.object) || null
  },

  async getUserDataInfo(): Promise<IUserDataInfo> {
    const response = await httpClient.get<
      ConexaResponse<IUserDataInfoResponse>
    >('/integration/app-paciente/dados-pessoais/obter-dados-basicos')
    const rawUserDataInfo = response?.data?.object
    const userDataInfo: IUserDataInfo = {
      name: rawUserDataInfo?.nome,
      birthDate: rawUserDataInfo?.dataNascimento,
      cpf: rawUserDataInfo?.cpf,
      phoneNumber: rawUserDataInfo?.celular,
      email: rawUserDataInfo?.email,
      sex: rawUserDataInfo?.sexo,
      streetName: rawUserDataInfo?.endereco || '',
      complement: rawUserDataInfo?.complemento || '',
      cep: rawUserDataInfo?.cep || '',
      formattedCep: rawUserDataInfo?.cepFormatado || '',
      city: rawUserDataInfo?.cidade || '',
      neighborhood: rawUserDataInfo?.bairro || '',
      number: rawUserDataInfo?.numeroLogradouro || '',
      state: rawUserDataInfo?.uf || '',
      contactsEmergency: {
        relationship: rawUserDataInfo?.contatoEmergencia?.relacao,
        fullName: rawUserDataInfo?.contatoEmergencia?.nomeCompleto,
        cellphone: rawUserDataInfo?.contatoEmergencia?.telefone,
        emergencyContactId:
          rawUserDataInfo?.contatoEmergencia?.idContatoEmergencia,
      },
    }
    return userDataInfo || null
  },
  async magicLinkLogin(token: string): Promise<IUser[]> {
    const response = await httpClient.post<MagicLinkLoginResponse>(
      `/integration/app-paciente/usuario/login/link-magico-paciente/`,
      {
        tokenId: token,
        audience: 'WEB',
      },
    )

    return response?.data?.object || []
  },

  async magicLinkLoginMultiprofile(redirectToken: string): Promise<IUser[]> {
    const token = import.meta.env.VITE_MAGIC_LINK_MULTIPROFILE_TOKEN
    const response = await httpClient.get<MagicLinkLoginResponse>(
      `/cnx/paciente/magic-link/${redirectToken}/v1/`,
      {
        headers: {
          token,
        },
      },
    )

    return response?.data?.object || []
  },

  async acceptTerm(data: TermsAndConditionsForm): Promise<void> {
    const formattedData = SaveAcceptTermData.toPersistence(data)

    await httpClient.post(
      '/integration/app-paciente/usuario/aceitar-termo-completando-dados',
      formattedData,
    )
  },

  validateToken: async (token: string): Promise<void> => {
    await httpClient.get('/integration/app-paciente/usuario/validar-token', {
      headers: {
        token,
      },
    })
  },

  authMagicLink: async (token: string): Promise<string | null> => {
    const response = await httpClient.get(
      `/integration/app-paciente/usuario/autenticacao-tokenid/${token}`,
    )

    return response?.data?.object || null
  },

  magicLinkByAccount: async (
    token: string,
  ): Promise<MagicLinkByAccount | null> => {
    const response = await httpClient.get(
      `/integration/app-paciente/usuario/autenticacao-tokenid/conta/${token}`,
    )

    return GetAuthMagicLinkByAccount.toDomain(response?.data?.object) || null
  },

  async getUsersProfilesFlowByAccount(token: string): Promise<IUser[]> {
    const response = await httpClient.get<MagicLinkLoginResponse>(
      `/integration/app-paciente/usuario/login/web/conta`,
      {
        headers: {
          token: token,
          audience: 'WEB',
        },
      },
    )

    return response?.data?.object || []
  },

  checkTermsAcceptance: async (userToken?: string): Promise<boolean> => {
    const token = userToken || getCache<string>('token')
    const response = await httpClient.get(
      '/integration/app-paciente/usuario/termo-aceito',
      {
        headers: {
          token,
        },
      },
    )

    return response.data.object
  },

  sendChangePasswordEmail: async (
    email: string,
    captchaToken: string,
  ): Promise<boolean> => {
    const response = await httpClient.post(
      '/usuario/esqueciSenha/paciente',
      {
        email,
      },
      {
        headers: {
          captcha: captchaToken,
        },
      },
    )

    return response?.data?.object || false
  },

  verifyPassword: async (
    password: string,
    captchaToken: string,
  ): Promise<boolean> => {
    const response = await httpClient.post(
      '/integration/app-paciente/usuario/verificarsenha',
      {
        senha: password,
      },
      {
        headers: {
          captcha: captchaToken,
        },
      },
    )

    return response?.data?.object
  },

  changePassword: async (
    password: string,
    confirmPassword: string,
  ): Promise<boolean> => {
    const response = await httpClient.post(
      '/integration/app-paciente/usuario/modificar-senha',
      {
        senha: password,
        csenha: confirmPassword,
      },
    )

    return response?.data?.object || false
  },

  validateFirstAccess: async (token: string): Promise<boolean> => {
    const response = await httpClient.post(
      'integration/app-paciente/usuario/atualizar-primeiro-acesso',
      {},
      {
        headers: {
          token,
        },
      },
    )
    return response?.data?.object
  },

  sendGeolocation: async (data: IGeolocation): Promise<boolean> => {
    const response = await httpClient.post(
      'integration/app-paciente/geolocalizacao/salvar',
      data,
    )
    return response?.data?.object
  },

  sendTwoFactorAuthCode: async (): Promise<SendTwoFactorAuthCode> => {
    const response = await httpClient.get<
      ConexaResponse<SendTwoFactorAuthCodeResponse>
    >('/integration/app-paciente/autenticacao2fa/enviar-codigo')

    return {
      codeSentAt: response?.data?.object?.momentoCriacao,
      attemptsCount: response?.data?.object?.qtdTentativas,
    }
  },

  validateTwoFactorAuthCode: async (code: string): Promise<boolean> => {
    const response = await httpClient.get<ConexaResponse<boolean>>(
      `/integration/app-paciente/autenticacao2fa/verificar-codigo/${code}`,
    )

    return response?.data?.object
  },

  confirmAppointment: async (
    data: AppointmentConfirmationParams,
  ): Promise<AppointmentConfirmation> => {
    const response = await httpClient.post(
      `/atendimento/confirmar/v2/${data?.attendanceToken}`,
      {},
      {
        headers: {
          token: data?.userToken,
        },
      },
    )
    return GetAppointmentConfirmationData.toDomain(response?.data?.object)
  },

  logout: async () => {
    return await httpClient.post('/integration/app-paciente/usuario/logout', {
      audience: 'WEB',
    })
  },

  getLegalGuardianRelationship: async (
    token: string,
  ): Promise<LegalGuardianRelationship[]> => {
    const response = await httpClient.get(
      '/termo-aceite-responsavel-legal/grau-parentesco',
      {
        headers: {
          token: token,
        },
      },
    )
    return response.data.object
  },

  checkLegalGuardianData: async (
    idPatient: number,
  ): Promise<MinorsTermData> => {
    const response = await httpClient.get(
      `/termo-aceite-responsavel-legal/paciente/${idPatient}`,
    )
    return GetMinorsTermAcceptanceData.toDomain(response.data.object)
  },

  sendMinorGuardianData: async (data: MinorAccessRequestData) => {
    const token = data?.token || getCache<string>('token')
    const dataFormatted = SendMinorAccessRequest.toPersistence(data)

    const {
      data: { object },
    } = await httpClient.post(
      `/termo-aceite-responsavel-legal`,
      dataFormatted,
      {
        headers: {
          token: token,
        },
      },
    )

    return object
  },

  checkMinorsTermAcceptance: async (token: string): Promise<boolean> => {
    const response = await httpClient.get(
      '/integration/app-paciente/usuario/termo-aceito/menor-idade',
      {
        headers: {
          token: token,
        },
      },
    )
    return response.data.object
  },
}
